import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Badge } from '@vaiu/ui/badge';
import { Progress } from '@vaiu/ui/progress';
import { 
  Send, 
  Bot,
  User,
  Sparkles,
  Store,
  Users,
  TrendingUp,
  Heart,
  Gift,
  Target,
  ArrowRight,
  CheckCircle,
  Building,
  Handshake,
  Megaphone,
  Crown,
  Coins
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
// import VaiuLogo from './shared/VaiuLogo';

interface OnboardingMessage {
  id: string;
  text: string;
  type: 'user' | 'system';
  timestamp: Date;
  options?: string[];
  expectsInput?: boolean;
  businessType?: string;
}

interface BusinessProfile {
  hasBusinessInterest: boolean;
  businessType: 'owner' | 'employee' | 'none';
  businessSize: 'solo' | 'small' | 'medium' | 'large';
  businessCategory: string;
  sellsOnline: boolean;
  sellsInPerson: boolean;
  hasEmployees: boolean;
  businessChallenges: string[];
  merchantWishlist: string[];
  growthGoals: string[];
  completedAt: Date;
}

interface OnboardingState {
  currentStep: number;
  totalSteps: number;
  businessProfile: Partial<BusinessProfile>;
  currentQuestion: string;
  awaitingResponse: boolean;
  completed: boolean;
}

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user, fetchProfile } = useAuth();
  const { toast } = useToast();
  
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    currentStep: 0,
    totalSteps: 8,
    businessProfile: {},
    currentQuestion: '',
    awaitingResponse: false,
    completed: false
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Onboarding conversation flow
  const onboardingSteps = [
    {
      id: 'welcome',
      message: `🎉 Welcome, ${user?.username || 'friend'}!\n\nI'm your personal guide to help you discover how we can transform the way you handle money and grow your business network.\n\nThis will take just 3-4 minutes, and I promise it'll be worth your time! Ready to get started?`,
      options: ['Yes, let\'s go!', 'Tell me more first'],
      expectsInput: false
    },
    {
      id: 'business_interest',
      message: `Great! Let's start with you. 🏪\n\nDo you own a business, work for one, or sell anything online or in person? This helps me understand how Vaiu can best serve you.`,
      options: ['I own a business', 'I work for a business', 'I sell things sometimes', 'Not really, just personal use'],
      expectsInput: false
    },
    {
      id: 'business_details',
      message: `Interesting! Tell me more about your business situation:`,
      expectsInput: true
    },
    {
      id: 'business_size',
      message: `Got it! And how many people are involved in the selling/business side?`,
      options: ['Just me (solo)', 'Small team (2-10 people)', 'Medium business (11-50)', 'Large business (50+)'],
      expectsInput: false
    },
    {
      id: 'business_challenges',
      message: `Now here's where it gets exciting! 🚀\n\nWhat's your biggest challenge when it comes to:\n• Getting paid by customers\n• Managing business expenses\n• Growing your customer base\n• Staying connected with customers\n\nTell me what frustrates you most:`,
      expectsInput: true
    },
    {
      id: 'merchant_wishlist',
      message: `Perfect! Now imagine this: What if you could help businesses you love DOUBLE their profits while saving money yourself? 💰\n\nIs there a business you frequent that you'd love to see succeed more? (Restaurant, shop, service provider, etc.)`,
      expectsInput: true
    },
    {
      id: 'network_power',
      message: `This is where Vaiu gets revolutionary! 🌟\n\nYou're not just a customer anymore - you're a NETWORK BUILDER. Here's what you can do:\n\n🎯 **Request merchants** to join Vaiu and earn rewards\n💝 **Help businesses** cut payment fees and boost profits\n🔗 **Stay connected** with customers without sharing personal info\n🏆 **Earn rewards** for growing the network\n\nSound interesting?`,
      options: ['This sounds amazing!', 'Tell me more about rewards', 'How does it help businesses?'],
      expectsInput: false
    },
    {
      id: 'growth_goals',
      message: `Excellent! Last question: What would success look like for you with Vaiu?\n\nThink about:\n• Earning extra income through referrals\n• Building a network of businesses you love\n• Helping your community grow\n• Having more control over your payment ecosystem\n\nWhat excites you most?`,
      expectsInput: true
    },
    {
      id: 'completion',
      message: `🎊 Amazing! You're all set!\n\nBased on what you've told me, here's your personalized Vaiu experience:\n\n✨ **Your Role**: Network Advocate\n🎯 **Your Mission**: Help grow businesses you care about\n💰 **Your Rewards**: Earn points for every merchant you help onboard\n🌟 **Your Impact**: Transform your local payment ecosystem\n\nReady to start making a difference?`,
      options: ['Show me how to get started!', 'Take me to my dashboard'],
      expectsInput: false
    }
  ];

  useEffect(() => {
    // Start onboarding conversation
    if (messages.length === 0) {
      startOnboarding();
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Focus input when expecting response
    if (onboardingState.awaitingResponse && inputRef.current) {
      inputRef.current.focus();
    }
  }, [onboardingState.awaitingResponse]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (text: string, type: 'user' | 'system', options?: string[], expectsInput?: boolean) => {
    const newMessage: OnboardingMessage = {
      id: Date.now().toString(),
      text,
      type,
      timestamp: new Date(),
      options,
      expectsInput
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  const startOnboarding = () => {
    const firstStep = onboardingSteps[0];
    addMessage(firstStep.message, 'system', firstStep.options, firstStep.expectsInput);
    setOnboardingState(prev => ({
      ...prev,
      currentStep: 0,
      currentQuestion: firstStep.id,
      awaitingResponse: true
    }));
  };

  const handleOptionSelect = (option: string) => {
    // Add user's selection as message
    addMessage(option, 'user');
    
    // Handle personal completion options
    if (onboardingState.currentQuestion === 'personal_completion') {
      if (option.toLowerCase().includes('dashboard')) {
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/');
        }, 500);
        return;
      } else if (option.toLowerCase().includes('tokens')) {
        // Navigate to core page (token creation)
        setTimeout(() => {
          navigate('/app');
        }, 500);
        return;
      }
    }
    
    // Handle regular completion options
    if (onboardingState.currentQuestion === 'completion') {
      if (option.toLowerCase().includes('dashboard')) {
        setTimeout(() => {
          navigate('/');
        }, 500);
        return;
      } else if (option.toLowerCase().includes('get started')) {
        setTimeout(() => {
          navigate('/app');
        }, 500);
        return;
      }
    }
    
    processUserResponse(option);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    const userInput = input.trim();
    setInput('');
    addMessage(userInput, 'user');
    processUserResponse(userInput);
  };

  const processUserResponse = async (response: string) => {
    setIsProcessing(true);
    
    try {
      // Update business profile based on current question
      let updatedProfile = { ...onboardingState.businessProfile };
      
      switch (onboardingState.currentQuestion) {
        case 'welcome':
          // Just continue to next step
          break;
        case 'business_interest':
          updatedProfile.businessType = response.toLowerCase().includes('own') ? 'owner' : 
                                      response.toLowerCase().includes('work') ? 'employee' : 'none';
          updatedProfile.hasBusinessInterest = !response.toLowerCase().includes('not really');
          
          // If user selected "Not really, just personal use", skip business questions and complete onboarding
          if (response.toLowerCase().includes('not really')) {
            // Set as personal user and complete onboarding
            updatedProfile.businessType = 'none';
            updatedProfile.hasBusinessInterest = false;
            
            // Show completion message for personal users
            setTimeout(() => {
              addMessage(
                `🎉 Perfect! You're all set for personal use!\n\nAs a personal user, you can:\n\n💳 **Create and redeem payment tokens** for secure transactions\n🏪 **Request your favorite merchants** to join\n🎁 **Earn rewards** when businesses you refer join the network\n📱 **Enjoy seamless payments** without sharing personal details\n\nReady to get started?`,
                'system',
                ['Take me to my dashboard', 'Show me how to create tokens'],
                false
              );
              
              setOnboardingState(prev => ({
                ...prev,
                currentStep: onboardingSteps.length - 1, // Jump to completion
                currentQuestion: 'personal_completion',
                businessProfile: updatedProfile,
                awaitingResponse: true
              }));
            }, 1000);
            
            setIsProcessing(false);
            return;
          }
          break;
        case 'business_details':
          // Store additional business context
          updatedProfile.businessCategory = response;
          break;
        case 'business_size':
          updatedProfile.businessSize = response.toLowerCase().includes('just me') ? 'solo' :
                                      response.toLowerCase().includes('small') ? 'small' :
                                      response.toLowerCase().includes('medium') ? 'medium' : 'large';
          updatedProfile.hasEmployees = !response.toLowerCase().includes('just me');
          break;
        case 'business_challenges':
          updatedProfile.businessChallenges = [response];
          break;
        case 'merchant_wishlist':
          updatedProfile.merchantWishlist = [response];
          break;
        case 'growth_goals':
          updatedProfile.growthGoals = [response];
          break;
      }

      // Move to next step
      const nextStep = onboardingState.currentStep + 1;
      
      if (nextStep < onboardingSteps.length) {
        // Show next question
        setTimeout(() => {
          const nextStepData = onboardingSteps[nextStep];
          addMessage(nextStepData.message, 'system', nextStepData.options, nextStepData.expectsInput);
          
          setOnboardingState(prev => ({
            ...prev,
            currentStep: nextStep,
            currentQuestion: nextStepData.id,
            businessProfile: updatedProfile,
            awaitingResponse: true
          }));
        }, 1000);
      } else {
        // Complete onboarding
        await completeOnboarding(updatedProfile);
      }
      
    } catch (error) {
      console.error('Error processing onboarding response:', error);
      addMessage("Sorry, something went wrong. Let's continue!", 'system');
    } finally {
      setIsProcessing(false);
    }
  };

  const completeOnboarding = async (profile: Partial<BusinessProfile>) => {
    try {
      // Save business profile to user account
      const completeProfile: BusinessProfile = {
        ...profile,
        completedAt: new Date()
      } as BusinessProfile;

      // For now, just store in localStorage until backend endpoint is ready
      // TODO: Implement /profile/business endpoint in backend
      try {
        localStorage.setItem('vaiuBusinessProfile', JSON.stringify(completeProfile));
        console.log('✅ Business profile saved to localStorage');
        
        toast({
          title: "Onboarding Complete!",
          description: "Your business profile has been saved. Welcome!",
        });
      } catch (storageError) {
        console.warn('⚠️ Could not save to localStorage:', storageError);
        // Don't fail the onboarding just because of storage
      }

      // Mark onboarding as completed
      setOnboardingState(prev => ({
        ...prev,
        completed: true,
        businessProfile: completeProfile
      }));

      // Show completion message and options
      setTimeout(() => {
        // Force a full page reload to refresh auth state and user profile
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('❌ Error completing onboarding:', error);
      toast({
        title: "Onboarding Error",
        description: "There was an issue completing your onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getPersonalizedNextSteps = (profile: BusinessProfile): string => {
    if (profile.businessType === 'owner') {
      return `As a business owner, you can:\n• Request your suppliers to join Vaiu\n• Invite your customers to pay via Vaiu\n• Earn rewards for growing your business network\n• Save on payment processing fees`;
    } else if (profile.businessType === 'employee') {
      return `As someone who works with businesses, you can:\n• Suggest Vaiu to your employer\n• Request favorite merchants to join\n• Earn rewards for successful referrals\n• Build your professional network`;
    } else {
      return `As a consumer advocate, you can:\n• Request your favorite businesses to join Vaiu\n• Earn rewards for successful merchant onboarding\n• Help build a better payment ecosystem\n• Save money with exclusive merchant offers`;
    }
  };

  const handleFinalAction = (action: string) => {
    if (action.includes('get started') || action.includes('requesting')) {
      // Navigate to merchant request feature
      navigate('/merchant-requests');
    } else if (action.includes('dashboard')) {
      navigate('/');
    } else if (action.includes('rewards')) {
      navigate('/rewards');
    } else {
      // Default fallback - go to dashboard
      navigate('/');
    }
  };

  const renderMessage = (message: OnboardingMessage) => {
    const isUser = message.type === 'user';
    
    return (
      <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
        <div className={`flex items-start space-x-3 max-w-[85%] ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          {/* Avatar */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isUser ? 'bg-blue-500' : 'bg-gradient-to-br from-green-500 to-emerald-600'
          }`}>
            {isUser ? (
              <User className="w-5 h-5 text-white" />
            ) : (
              <Bot className="w-5 h-5 text-white" />
            )}
          </div>
          
          {/* Message bubble */}
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-blue-500 text-white' 
              : 'bg-white text-gray-800 border border-gray-200 shadow-sm'
          }`}>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.text}</div>
            
            {/* Options */}
            {message.options && !isUser && (
              <div className="mt-3 space-y-2">
                {message.options.map((option, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (onboardingState.completed) {
                        handleFinalAction(option);
                      } else {
                        handleOptionSelect(option);
                      }
                    }}
                    disabled={isProcessing}
                    className="w-full justify-start text-left bg-gray-50 hover:bg-gray-100 border-gray-200"
                  >
                    {option}
                  </Button>
                ))}
              </div>
            )}
            
            <div className={`text-xs mt-2 ${isUser ? 'text-blue-100' : 'text-gray-500'}`}>
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const progress = ((onboardingState.currentStep + 1) / onboardingState.totalSteps) * 100;

  return (
    <div className="min-h-screen page-background">
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* <VaiuLogo size="sm" /> */}
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <span>Welcome</span>
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Let's get you set up for success
                  </p>
                </div>
              </div>
              <Badge variant="secondary" className="px-3 py-1">
                <Crown className="w-3 h-3 mr-1" />
                New Member
              </Badge>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Setup Progress</span>
                <span>{onboardingState.currentStep + 1} of {onboardingState.totalSteps}</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardHeader>
        </Card>

        {/* Chat Area */}
        <Card className="mb-6">
          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-96 overflow-y-auto p-6 space-y-4">
              {messages.map(renderMessage)}
              {isProcessing && (
                <div className="flex justify-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-white rounded-2xl px-4 py-3 border border-gray-200">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input Area */}
            {onboardingState.awaitingResponse && !onboardingState.completed && (
              <div className="border-t p-4">
                <form onSubmit={handleSubmit} className="flex space-x-3">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your response..."
                    disabled={isProcessing}
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={isProcessing || !input.trim()}
                    size="sm"
                    className="px-4"
                  >
                    {isProcessing ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Features Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <Store className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-blue-800">Request Merchants</h3>
              <p className="text-sm text-blue-700">Help businesses join Vaiu and earn rewards</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-green-800">Build Network</h3>
              <p className="text-sm text-green-700">Connect businesses and customers</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 text-center">
              <Coins className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h3 className="font-semibold text-purple-800">Earn Rewards</h3>
              <p className="text-sm text-purple-700">Get paid for growing the network</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow; 